import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Card, Button, Input } from 'reactstrap';
import CustomModal from '../../../../../../../Components/Modals/CustomModal';
import AssetAnnontationsReplyService from '../../../../../../../../Service/DashboardServices/ContentManagementServices/AssetAnnontationsReplyService';

export const createLocalReply = (annotationId, comment) => ({
  id: `pending-${Date.now()}`,
  annotationId,
  comment,
  pending: true,
  createdAt: new Date().toISOString(),
  createdBy: 'You',
});

export const collectPendingReplies = (annotations) => {
  const pending = [];
  (annotations || []).forEach((ann) => {
    (ann.replies || []).forEach((reply) => {
      if (reply && reply.pending) {
        pending.push({ annotationId: ann.id, comment: reply.comment });
      }
    });
  });
  return pending;
};

export const mergeStagedAfterReplySync = (prev, syncedAnnotations) => {
  const clean = (syncedAnnotations || []).map(({ isNew, isEdited, ...ann }) => ({
    ...ann,
    replies: Array.isArray(ann.replies) ? ann.replies : [],
  }));
  if (!clean.length && prev && prev.length) return prev;

  const unsaved = (prev || []).filter((ann) => ann && (ann.isNew || ann.isEdited));
  const prevRepliesById = {};
  (prev || []).forEach((ann) => {
    if (ann && ann.id) prevRepliesById[ann.id] = ann.replies || [];
  });

  const merged = clean.map((ann) => {
    const unsavedMatch = unsaved.find((u) => u.id === ann.id);
    if (unsavedMatch) return unsavedMatch;
    const pendingReplies = (prevRepliesById[ann.id] || []).filter((r) => r && r.pending);
    return {
      ...ann,
      replies:
        (Array.isArray(ann.replies) && ann.replies.length)
          ? ann.replies
          : (pendingReplies.length ? pendingReplies : (prevRepliesById[ann.id] || [])),
    };
  });

  unsaved.forEach((ann) => {
    if (!merged.some((m) => m.id === ann.id)) {
      merged.push(ann);
    }
  });

  return merged;
};

function AnnotationCanvas({
  image,
  selectedTool,
  selectedColor,
  onStageAnnotation,
  onEditAnnotation,
  onDeleteAnnotation,
  onDeleteAndSave,
  onReplyAnnotation,
  annotations = [],
  token,
}) {
  const canvasRef = useRef(null);
  const imgRef = useRef(new window.Image());
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentPath, setCurrentPath] = useState([]);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [comment, setComment] = useState("");
  const [drawnAnnotation, setDrawnAnnotation] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editComment, setEditComment] = useState('');
  const [replyingId, setReplyingId] = useState(null);
  const [replyComment, setReplyComment] = useState('');
  const [annotationReplies, setAnnotationReplies] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [hoveredAnnotation, setHoveredAnnotation] = useState(null);

  useEffect(() => {
    setAnnotationReplies((prev) => {
      const repliesMap = { ...(prev || {}) };
      (Array.isArray(annotations) ? annotations : []).forEach((ann) => {
        if (!ann || !ann.id) return;
        const incoming = Array.isArray(ann.replies) ? ann.replies : null;
        // Prefer non-empty incoming replies; keep existing local replies if backend sent empty
        if (incoming && incoming.length > 0) {
          repliesMap[ann.id] = incoming;
        } else if (!repliesMap[ann.id]) {
          repliesMap[ann.id] = incoming || [];
        }
      });
      return repliesMap;
    });
  }, [annotations]);

  useEffect(() => {
    setImgLoaded(false);
    const imageElement = imgRef.current;
    const handleLoad = () => {
      const maxWidth = 800; // Increased size for better viewing in a modal
      const maxHeight = 600;
      const aspect = imageElement.width / imageElement.height;
      let w, h;

      if (aspect > maxWidth / maxHeight) {
        w = maxWidth;
        h = maxWidth / aspect;
      } else {
        h = maxHeight;
        w = maxHeight * aspect;
      }

      setCanvasSize({ width: w, height: h });

      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        canvasRef.current.width = w;
        canvasRef.current.height = h;
        ctx.drawImage(imageElement, 0, 0, w, h);
        setImgLoaded(true);
      }
    };
    imageElement.onload = handleLoad;
    imageElement.src = image.filePath;

    // Cleanup function
    return () => {
        imageElement.onload = null;
    };
  }, [image.filePath]);


  // --- MOUSE AND COORDINATE HELPERS ---
  const getMouse = e => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * canvasRef.current.width / rect.width,
      y: (e.clientY - rect.top) * canvasRef.current.height / rect.height
    };
  };

  const canvasToNorm = ({ x, y }) => ({
    x: x / canvasSize.width,
    y: y / canvasSize.height
  });


  // --- DRAWING HANDLERS ---
  function handleDown(e) {
    if (!imgLoaded || !selectedTool || selectedTool === 'default' || drawnAnnotation) return;
    setIsDrawing(true);
    const pos = getMouse(e);
    setStartPoint(pos);
    if (selectedTool === 'freeform') setCurrentPath([pos]);
  }

  function handleMove(e) {
    if (!isDrawing || !imgLoaded) return;
    const ctx = canvasRef.current.getContext('2d');

    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    ctx.drawImage(imgRef.current, 0, 0, canvasSize.width, canvasSize.height);

    annotations&&annotations.forEach(annotation => drawAnnotation(ctx, annotation));

    ctx.strokeStyle = selectedColor;
    ctx.lineWidth = 2;
    ctx.fillStyle = "transparent";

    const pos = getMouse(e);
    if (selectedTool === 'circle') {
      const r = Math.hypot(pos.x - startPoint.x, pos.y - startPoint.y);
      ctx.beginPath();
      ctx.arc(startPoint.x, startPoint.y, r, 0, 2 * Math.PI);
      ctx.stroke();
    } else if (selectedTool === 'rectangle') {
      ctx.beginPath();
      ctx.rect(startPoint.x, startPoint.y, pos.x - startPoint.x, pos.y - startPoint.y);
      ctx.stroke();
    } else if (selectedTool === "freeform") {
      const newPath = [...currentPath, pos];
      setCurrentPath(newPath);
      ctx.beginPath();
      ctx.moveTo(newPath[0].x, newPath[0].y);
      newPath.forEach(point => ctx.lineTo(point.x, point.y));
      ctx.stroke();
    }
  }

  function handleUp(e) {
    if (!isDrawing || !selectedTool || selectedTool === 'default') return;
    setIsDrawing(false);
    const pos = getMouse(e);
    let coordinates = null;

    if (selectedTool === 'circle') {
      const r = Math.hypot(pos.x - startPoint.x, pos.y - startPoint.y);
      const normCenter = canvasToNorm(startPoint);
      const normRadius = r / Math.sqrt(canvasSize.width * canvasSize.height); // Normalize radius against diagonal
      coordinates = [normCenter.x, normCenter.y, normRadius];
    } else if (selectedTool === 'rectangle') {
      const normStart = canvasToNorm(startPoint);
      const normEnd = canvasToNorm(pos);
      coordinates = [normStart.x, normStart.y, normEnd.x - normStart.x, normEnd.y - normStart.y];
    } else if (selectedTool === "freeform") {
      const flatArray = currentPath.flatMap(pt => {
        const normPt = canvasToNorm(pt);
        return [normPt.x, normPt.y];
      });
      coordinates = flatArray;
      setCurrentPath([]);
    }

    if (coordinates) {
      setDrawnAnnotation({
        type: selectedTool,
        color: selectedColor,
        coordinates: coordinates
      });
      redrawCanvas();
    }
  }

  // --- ANNOTATION DRAWING LOGIC ---
  const drawAnnotationShapePath = useCallback((ctx, annotation) => {
    const coords = annotation && annotation.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return;
    switch (annotation.type) {
      case 'circle': {
        if (coords.length < 3) return;
        const centerX = coords[0] * canvasSize.width;
        const centerY = coords[1] * canvasSize.height;
        const radius = coords[2] * Math.sqrt(canvasSize.width * canvasSize.height);
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        break;
      }
      case 'rectangle': {
        if (coords.length < 4) return;
        const x = coords[0] * canvasSize.width;
        const y = coords[1] * canvasSize.height;
        const width = coords[2] * canvasSize.width;
        const height = coords[3] * canvasSize.height;
        ctx.rect(x, y, width, height);
        break;
      }
      case 'freeform': {
        if (coords.length >= 4) {
          ctx.moveTo(coords[0] * canvasSize.width, coords[1] * canvasSize.height);
          for (let i = 2; i < coords.length; i += 2) {
            ctx.lineTo(coords[i] * canvasSize.width, coords[i + 1] * canvasSize.height);
          }
        }
        break;
      }
      default:
        break;
    }
  }, [canvasSize]);

  function drawAnnotation(ctx, annotation, isHighlighted = false) {
    if (!annotation || !Array.isArray(annotation.coordinates)) return;
    ctx.strokeStyle = annotation.color || '#ff4444';
    ctx.lineWidth = isHighlighted ? 4 : 2;
    ctx.shadowColor = isHighlighted ? (annotation.color || '#ff4444') : 'transparent';
    ctx.shadowBlur = isHighlighted ? 10 : 0;
    ctx.fillStyle = "transparent";

    ctx.beginPath();
    drawAnnotationShapePath(ctx, annotation);
    ctx.stroke();
    // Reset shadow for next drawings
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }

  const redrawCanvas = useCallback(() => {
    if (!canvasRef.current || !imgLoaded || !imgRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    ctx.drawImage(imgRef.current, 0, 0, canvasSize.width, canvasSize.height);

    if (hoveredAnnotation) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);

      ctx.save();
      ctx.beginPath();
      drawAnnotationShapePath(ctx, hoveredAnnotation);
      ctx.clip();
      ctx.drawImage(imgRef.current, 0, 0, canvasSize.width, canvasSize.height);
      ctx.restore();

      drawAnnotation(ctx, hoveredAnnotation, true);
    } else {
      annotations &&annotations.forEach(annotation => drawAnnotation(ctx, annotation));
      if (drawnAnnotation) {
        drawAnnotation(ctx, drawnAnnotation);
      }
    }
  }, [annotations, imgLoaded, hoveredAnnotation, drawnAnnotation, canvasSize, drawAnnotationShapePath]);

  useEffect(() => {
    if (imgLoaded) {
      redrawCanvas();
    }
  }, [annotations, imgLoaded, hoveredAnnotation, drawnAnnotation, redrawCanvas]);


  // --- COMMENT AND EDITING HANDLERS (MODIFIED) ---

  const handleCommentBlur = () => {
    if (drawnAnnotation && comment.trim()) {
      onStageAnnotation(
        drawnAnnotation.coordinates,
        drawnAnnotation.type,
        drawnAnnotation.color,
        comment.trim()
      );
      setComment("");
      setDrawnAnnotation(null);
    } else {
      handleClearAnnotation();
    }
  };

  function handleClearAnnotation() {
    setDrawnAnnotation(null);
    setComment("");
    redrawCanvas();
  }
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const handleStartEdit = (annotation) => {
    setReplyingId(null);
    setReplyComment('');
    setEditingId(annotation.id);
    setEditComment(annotation.comment);
  };

  const handleSaveEdit = () => {
    if (onEditAnnotation) {
      onEditAnnotation(editingId, editComment);
    }
    setEditingId(null);
    setEditComment('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditComment('');
  };

  const handleDeleteClick = (annotationId) => {
    setShowDeleteConfirm(annotationId);
  };

  const handleConfirmDelete = () => {
    if (onDeleteAnnotation) {
      onDeleteAnnotation(showDeleteConfirm);
    } else if (onDeleteAndSave) {
      onDeleteAndSave(showDeleteConfirm);
    }
    setShowDeleteConfirm(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const handleReplyClick = (annotation) => {
    setEditingId(null);
    setEditComment('');
    setReplyingId(annotation.id);
    setReplyComment('');
  };

  const handleCancelReply = () => {
    setReplyingId(null);
    setReplyComment('');
  };

  const handleSendReply = (annotation) => {
    if (!replyComment.trim()) return;

    const replyText = replyComment.trim();
    const replyData = {
      id: annotation.id,
      comment: replyText,
      assetId: image.assetId || image.id,
      assetsAnnotationsId: image.assetsAnnotationsId || image.cmAssetsAnnotationsId || null,
      integerId: image.integerId,
      assetCategory: image.assetCategory,
      assetIdentifier: image.assetIdentifier,
      projectId: image.projectId,
      subCategoryId: image.assetSubCategory || image.subCategoryId,
      asinList: image.asinList || '',
      submissionAssetId: image.submissionAssetId || image.submissionId || null,
      submissionId: image.submissionDetailId || null,
    };

    if (annotation.isNew) {
      const localReply = createLocalReply(annotation.id, replyText);
      setAnnotationReplies((prev) => ({
        ...(prev || {}),
        [annotation.id]: [...((prev && prev[annotation.id]) || []), localReply],
      }));
      if (onReplyAnnotation) {
        onReplyAnnotation({ localOnly: true, annotationId: annotation.id, comment: replyText, reply: localReply });
      }
      setReplyingId(null);
      setReplyComment('');
      return;
    }

    AssetAnnontationsReplyService(token, replyData).then((res) => {
      if (!res) return;

      const findAnnotationsForImage = (fileList) => {
        if (!Array.isArray(fileList)) return null;
        const targetId = image.assetId || image.id;
        const parent = fileList.find((file) => file.id === targetId);
        if (parent && Array.isArray(parent.annotations)) return parent.annotations;
        for (let i = 0; i < fileList.length; i += 1) {
          const versions = fileList[i].versionAssets || [];
          const version = versions.find((v) => String(v.id) === String(targetId));
          if (version && Array.isArray(version.annotations)) return version.annotations;
        }
        return null;
      };

      if (res.annotationReplies && typeof res.annotationReplies === 'object') {
        setAnnotationReplies((prev) => ({
          ...(prev || {}),
          ...res.annotationReplies,
        }));
      } else {
        const updatedFileList = res.fileList && res.fileList.fileList;
        const updatedAssetList = res.assetList && res.assetList.assetList;
        const updatedAnnotations =
          findAnnotationsForImage(updatedFileList) ||
          (Array.isArray(updatedAssetList)
            ? (updatedAssetList.find(
                (file) => file.assetId === (image.assetId || image.id) || file.id === (image.assetId || image.id)
              ) || {}).annotations
            : null);

        if (Array.isArray(updatedAnnotations)) {
          const repliesMap = {};
          updatedAnnotations.forEach((ann) => {
            if (ann && ann.id) {
              repliesMap[ann.id] = Array.isArray(ann.replies) ? ann.replies : [];
            }
          });
          setAnnotationReplies((prev) => ({
            ...(prev || {}),
            ...repliesMap,
          }));
        } else {
          // Keep reply visible even if list payload is incomplete
          setAnnotationReplies((prev) => {
            const existing = (prev && prev[annotation.id]) || [];
            return {
              ...(prev || {}),
              [annotation.id]: [
                ...existing,
                {
                  id: `temp-${Date.now()}`,
                  annotationId: annotation.id,
                  comment: replyText,
                  createdAt: new Date().toISOString(),
                  createdBy: 'You',
                },
              ],
            };
          });
        }
      }

      if (onReplyAnnotation) {
        onReplyAnnotation(res, replyData);
      }

      setReplyingId(null);
      setReplyComment('');
    });
  };

  return (
    <Row>
      <Col md={8}>
        <canvas
          ref={canvasRef}
          className="annotation-canvas"
          style={{
            cursor: selectedTool && selectedTool !== 'default' ? 'crosshair' : 'default',
            border: '2px solid #e5e7eb',
            borderRadius: '8px'
          }}
          onMouseDown={handleDown}
          onMouseMove={handleMove}
          onMouseUp={handleUp}
        />
      </Col>

      <Col md={4}>
        <div className='comments-section' style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {drawnAnnotation && (
            <div className="mb-3">
              <h6 className="">Add Comment</h6>
              <textarea
                className="form-control mb-1"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment and click away to save..."
                onBlur={handleCommentBlur}
                autoFocus
              />
            </div>
          )}

          {(Array.isArray(annotations) ? annotations : [])
            .filter((annotation) => annotation && annotation.id)
            .slice()
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
              .map((annotation, index) => {
                // Check if the annotation is new OR has been edited
                const hasUnsavedChanges = annotation.isNew || annotation.isEdited;

                // Define style for any unsaved comments (new or edited)
                const unsavedStyle = hasUnsavedChanges ? {
                  border: '2px solid #8be9a7',
                  borderRadius: '8px',
                  padding: '10px',
                  marginBottom: '10px',
                } : {
                  padding: '10px',
                  marginBottom: '10px'
                };

                return (
                  <div
                    key={annotation.id || `annotation-${index}`}
                    className='annotation-comments'
                    style={unsavedStyle}
                    onMouseEnter={() => !editingId && !replyingId && setHoveredAnnotation(annotation)}
                    onMouseLeave={() => setHoveredAnnotation(null)}
                  >
                    <div className='annotation-info mb-0'>
                      <span className="annotation-number">{index + 1}</span>
                      <span>{annotation.userName || "Anonymous"}</span>
                    </div>
                    <span>{formatDate(annotation.createdAt)}</span>
                    
                    <div>
                      {editingId === annotation.id ? (
                        <div>
                          <textarea
                            className="form-control"
                            value={editComment}
                            onChange={(e) => setEditComment(e.target.value)}
                            onBlur={handleSaveEdit} 
                            autoFocus
                          />
                        </div>
                      ) : (
                        <>
                          <b>{annotation.comment}</b>
                          <div>
                            <Button
                              color="link"
                              size="sm"
                              className="annotation-edit p-0"
                              onClick={() => handleStartEdit(annotation)}
                            >
                              Edit
                            </Button>
                            <Button
                              color="link"
                              size="sm"
                              className="annotation-delete p-0 ml-2"
                              onClick={() => handleDeleteClick(annotation.id)}
                            >
                              Delete
                            </Button>
                            <Button
                              color="link"
                              size="sm"
                              className="annotation-reply p-0 ml-2"
                              onClick={() => handleReplyClick(annotation)}
                            >
                              Reply
                            </Button>
                          </div>
                          {(annotationReplies[annotation.id] || []).length > 0 && (
                            <div className="annotation-replies mt-2">
                              {(annotationReplies[annotation.id] || []).map((reply) => (
                                <div key={reply.id || `${annotation.id}-${reply.createdAt}`} className="annotation-reply-item">
                                  <div className="d-flex justify-content-between">
                                    <small><b>{reply.createdBy || 'Anonymous'}</b></small>
                                    <small>{reply.createdAt ? formatDate(reply.createdAt) : ''}</small>
                                  </div>
                                  <div>{reply.comment}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {replyingId === annotation.id && (
                            <div className="mt-2">
                              <textarea
                                className="form-control mb-2"
                                value={replyComment}
                                onChange={(e) => setReplyComment(e.target.value)}
                                placeholder="Write a reply..."
                                autoFocus
                              />
                              <div className="d-flex justify-content-end">
                                <Button
                                  className="no-btn-background-color mr-2"
                                  size="sm"
                                  onClick={handleCancelReply}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  className="btn-background-color"
                                  size="sm"
                                  disabled={!replyComment.trim()}
                                  onClick={() => handleSendReply(annotation)}
                                >
                                  Send
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

          <CustomModal
            isOpen={!!showDeleteConfirm}
            toggle={handleCancelDelete}
            size="md"
            className="curve-popup"
            header={<p>Confirm Deletion</p>}
          >
            <Card className="curve-popup p-3">
              <Row>
                <Col md={12}>
                  <div>
                    <h6>Are you sure you want to delete this annotation? This action cannot be undone.</h6>
                  </div>
                  <div className='mb-2 mt-3 d-flex justify-content-end'>
                    <Button
                      className="no-btn-background-color mr-2"
                      onClick={handleCancelDelete}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="danger"
                      onClick={handleConfirmDelete}
                    >
                      Delete
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card>
          </CustomModal>
        </div>
      </Col>
    </Row>
  );
}

function PhotoDisplay({ image, annotations = [] }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageElement, setImageElement] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [hoveredAnnotation, setHoveredAnnotation] = useState(null);

  // Load and setup image
  useEffect(() => {
    if (!image || !image.filePath) return;
    const img = new Image();
    img.onload = () => {
      console.log("Image loaded successfully");
      setImageElement(img);
      setImageLoaded(true);
      setImageError(false);
    };
    img.onerror = (err) => {
      console.error("Image failed to load:", image.filePath, err);
      setImageError(true);
      setImageLoaded(false);
    };
    img.src = image.filePath;
  }, [image && image.filePath]);


  // Setup canvas dimensions
  useEffect(() => {
    if (!imageLoaded || !imageElement || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const maxWidth = 800;
    const maxHeight = 600;
    const aspectRatio = imageElement.naturalWidth / imageElement.naturalHeight;
    let width, height;
    if (aspectRatio > maxWidth / maxHeight) {
      width = Math.min(maxWidth, imageElement.naturalWidth);
      height = width / aspectRatio;
    } else {
      height = Math.min(maxHeight, imageElement.naturalHeight);
      width = height * aspectRatio;
    }
    canvas.width = width;
    canvas.height = height;
    setCanvasSize({ width, height });
  }, [imageLoaded, imageElement, canvasRef.current]);

  // Draw canvas content
  const drawCanvas = useCallback(() => {
    if (!imageElement || !canvasRef.current || canvasSize.width === 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image initially
    ctx.drawImage(imageElement, 0, 0, canvasSize.width, canvasSize.height);

    if (hoveredAnnotation) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);

      ctx.save();
      ctx.beginPath();
      drawAnnotationShapePath(ctx, hoveredAnnotation);
      ctx.clip();

      ctx.drawImage(imageElement, 0, 0, canvasSize.width, canvasSize.height);
      ctx.restore(); 

      ctx.save();
      ctx.strokeStyle = hoveredAnnotation.color || '#3b82f6';
      ctx.lineWidth = 4; 
      ctx.shadowColor = hoveredAnnotation.color || '#3b82f6';
      ctx.shadowBlur = 10;
      drawAnnotationShape(ctx, hoveredAnnotation);
      ctx.restore();
    } else {
      annotations && annotations.forEach(annotation => {
        ctx.save();
        ctx.strokeStyle = annotation.color || '#3b82f6';
        ctx.lineWidth = 2;
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        drawAnnotationShape(ctx, annotation);
        ctx.restore();
      });
    }

  }, [imageElement, canvasSize, annotations, hoveredAnnotation]);

  // Helper to draw the path for clipping (without stroke/fill)
  const drawAnnotationShapePath = (ctx, annotation) => {
    const coords = annotation && annotation.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return;
    switch (annotation.type) {
      case 'circle': {
        if (coords.length < 3) return;
        const centerX = coords[0] * canvasSize.width;
        const centerY = coords[1] * canvasSize.height;
        const radius = coords[2] * Math.sqrt(canvasSize.width * canvasSize.height);
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        break;
      }
      case 'rectangle': {
        if (coords.length < 4) return;
        const x = coords[0] * canvasSize.width;
        const y = coords[1] * canvasSize.height;
        const width = coords[2] * canvasSize.width;
        const height = coords[3] * canvasSize.height;
        ctx.rect(x, y, width, height);
        break;
      }
      case 'freeform': {
        if (coords.length >= 4) {
          ctx.moveTo(coords[0] * canvasSize.width, coords[1] * canvasSize.height);
          for (let i = 2; i < coords.length; i += 2) {
            ctx.lineTo(coords[i] * canvasSize.width, coords[i + 1] * canvasSize.height);
          }
          ctx.closePath();
        }
        break;
      }
      default:
        break;
    }
  };


  
  const drawAnnotationShape = (ctx, annotation) => {
    if (!annotation || !Array.isArray(annotation.coordinates)) return;
    const coords = annotation.coordinates;
    ctx.beginPath(); 
    switch (annotation.type) {
      case 'circle': {
        if (coords.length < 3) return;
        const centerX = coords[0] * canvasSize.width;
        const centerY = coords[1] * canvasSize.height;
        const radius = coords[2] * Math.sqrt(canvasSize.width * canvasSize.height);
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        break;
      }
      case 'rectangle': {
        if (coords.length < 4) return;
        const x = coords[0] * canvasSize.width;
        const y = coords[1] * canvasSize.height;
        const width = coords[2] * canvasSize.width;
        const height = coords[3] * canvasSize.height;
        ctx.rect(x, y, width, height);
        break;
      }
      case 'freeform': {
        if (coords.length >= 4) {
          ctx.moveTo(coords[0] * canvasSize.width, coords[1] * canvasSize.height);
          for (let i = 2; i < coords.length; i += 2) {
            ctx.lineTo(coords[i] * canvasSize.width, coords[i + 1] * canvasSize.height);
          }
        }
        break;
      }
      default:
        break;
    }
    ctx.stroke();
  };

  // Redraw canvas when dependencies change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Get mouse position relative to canvas
  const getMousePosition = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasSize.width / rect.width;
    const scaleY = canvasSize.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, [canvasSize]);

  // Modified to handle multiple circles
  const isPointInAnnotation = useCallback((point, annotation) => {
    const tolerance = 10;
    const coords = annotation.coordinates;
    const ctx = canvasRef.current.getContext('2d');

    // For precise hit detection, draw the path and use isPointInStroke
    ctx.save();
    ctx.beginPath();
    drawAnnotationShapePath(ctx, annotation);
    ctx.lineWidth = tolerance * 2; 
    const hit = ctx.isPointInStroke(point.x, point.y);
    ctx.restore();
    return hit;

  }, [canvasSize]);

  // Handle mouse movement
  const handleMouseMove = useCallback((e) => {
    if (canvasSize.width === 0 || !canvasRef.current) return;
    const mousePos = getMousePosition(e);
    const foundAnnotation = annotations && annotations.find(annotation => isPointInAnnotation(mousePos, annotation));
    if (foundAnnotation) {
      setHoveredAnnotation(foundAnnotation);
      canvasRef.current.style.cursor = 'pointer';
    } else {
      setHoveredAnnotation(null);
      canvasRef.current.style.cursor = 'default';
    }
  }, [annotations, getMousePosition, isPointInAnnotation, canvasSize.width]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setHoveredAnnotation(null);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'default';
    }
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    // Create a new Date object from the input string
    const date = new Date(dateString);
  
    // Get individual date and time components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed, so we add 1
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
  
    // Combine them into the desired format
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Loading state
  if (!imageLoaded && !imageError) {
    return (
      <div className="photo-display-loading">
        <div className="loading-spinner"></div>
        <p>Loading image...</p>
      </div>
    );
  }

  if (imageError) {
    return (
      <div className="photo-display-error">
        <div className="error-icon">⚠️</div>
        <p>Error loading image</p>
        <button className="retry-button" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="photo-container">
      {hoveredAnnotation && hoveredAnnotation.comment && (
        <div
          className="photo-annotation-tooltip"
          style={{ borderColor: hoveredAnnotation.color || "#3b82f6" }}
        >
          <div className="tooltip-header">{hoveredAnnotation.comment}</div>
          <div className="tooltip-footer">
            <span>{hoveredAnnotation.userName || "Anonymous"}</span>
            <span>{formatDate(hoveredAnnotation.createdAt)}</span>
          </div>
        </div>
      )}

      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="photo-canvas"
      />
    </div>
  );
}
export { PhotoDisplay };

const mapStateToProps = (state) => ({
  token: state.loggedUser.token,
});

export default connect(mapStateToProps)(AnnotationCanvas);